import React, { useRef, useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';

import { User } from '@paragon/contract-library';

import { isWhiteLabeledParagonUser, useAppWithLogInDataSelector } from '../../../../..';
import { logInWithCredentials } from '../../../actions/authenticationActions';
import { LoginDataSliceState } from '../../../store/logInDataSlice';
import { AlternateActionButton } from '../components/AlternateActionButton';
import { LoginForm } from '../components/LoginForm';
import { LoginInput } from '../components/LoginInput';

interface Props {
  readonly initialEmail?: string;

  readonly onLogin?: (user: User) => void;
  readonly onForgotPassword: (email: string) => void;
  readonly onLoginFailed: () => void;

  readonly onSignupClick: (email?: string) => void;
}

export const EmailAndPasswordEntry = ({
  initialEmail,
  onLogin,
  onForgotPassword,
  onLoginFailed,
  onSignupClick,
}: Props) => {
  const inWhiteLabeledContext = useAppWithLogInDataSelector(isWhiteLabeledParagonUser);

  const emailField = useRef<HTMLInputElement | null>(null);
  const passwordField = useRef<HTMLInputElement | null>(null);

  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();

  async function onSubmit() {
    const user = await dispatch(logInWithCredentials(email, password));

    if (user == null) {
      emailField.current?.focus();
      emailField.current?.select();

      setPassword('');

      onLoginFailed();

      return;
    }

    if (typeof onLogin === 'function') {
      onLogin(user);
    }
  }

  return (
    <>
      <LoginForm
        submitText="Log In"
        onSubmit={onSubmit}
        onSubmissionWillBegin={() => setIsSubmitting(true)}
        onSubmissionDidEnd={() => setIsSubmitting(false)}
      >
        <LoginInput autoFocus={!initialEmail} label="Email" inputRef={emailField} value={email} onChange={setEmail} />
        <LoginInput
          autoFocus={!!initialEmail}
          password
          label="Password"
          inputRef={passwordField}
          value={password}
          onChange={setPassword}
        />

        <AlternateActionButton onClick={() => onForgotPassword(email)}>Forgot Password?</AlternateActionButton>
      </LoginForm>

      {!isSubmitting && !inWhiteLabeledContext && (
        <Button variant="contained" color="secondary" fullWidth onClick={() => onSignupClick(email)}>
          Sign Up
        </Button>
      )}
    </>
  );
};
